'use client';

import { useState } from 'react';
import { PortalLink, useCart, useCommerceSource } from '@akademi/site-builder-runtime';
import { SavedListPicker, type SavedListPickerSource } from '@commerce/site-builder-blocks';
import { availabilityLabel } from '../home/availability';
import { formatMinor, totalOf, type LineTotal, type LiveMoney } from '../home/money';
import { solarText } from '../locales/useSolarText';
import { LISTING_GRID_CSS } from '../styles/listingGridCss';
import { BASE_CSS, TradeStyles } from '../styles/TradeStyles';
import {
  datasheetOf,
  formatPerWatt,
  specRows,
  totalOnBasis,
  wattageOf,
  type Wattage,
} from './listingFacts';
import type {
  SolarListingCard,
  SolarListingGridProps,
  VatBasis,
  VatDisplayState,
} from './types';

/**
 * What a card prices on when the host says nothing: the public view.
 *
 * <p>Identical to the storefront's own server snapshot of its VAT store — inc VAT, no
 * switch — so a host too old to pass `vat` draws what it drew before, and the first client
 * render matches the server's.
 */
const PUBLIC_VAT: VatDisplayState = { trade: false, basis: 'inc' };

/**
 * Allow Energy's own listing card, on `/c/…` and `/search`.
 *
 * <p><strong>Why the shop draws its own and does not skin the platform's.</strong> A theme
 * restyles commerce's grid through the `sf-card*` hooks it carries, and that reaches as far as
 * colour and geometry: it cannot add a line. The approved mockup
 * (`solar/docs/design/redesign-2026-10-01/catalogue.html`) puts four things on the card that
 * the platform card has no markup for at all — the manufacturer's part number, a three-row
 * specification microtable, the datasheet, and pence per watt — because an installer choosing
 * between fourteen panels compares those four and nothing else. Selecting another component's
 * children from outside and hoping they stay put is not a way to build that, so the vertical
 * supplies its own grid through the storefront's `catalogueComponents.productGrid` hook. The
 * routes, the data and the URLs stay commerce's; only the card is ours.
 *
 * <p>It owns the empty state too: "nothing matched" is part of how a listing looks.
 *
 * <p><strong>What it will not say.</strong> No stock wording the backend did not send, no
 * quantity-break or pallet claim (the mockup's pallet hint is illustrative, and nothing on a
 * listing card knows whether a product has a break), and no price where there is none — a
 * product with no price reads "Price on application", which is the host's own
 * `price.onRequest` string, not a figure invented here.
 */
export function SolarListingGrid({
  items,
  locale,
  messages,
  hrefFor,
  layout = 'trade',
  vat = PUBLIC_VAT,
}: SolarListingGridProps) {
  if (!items.length) {
    return (
      <div className="ae ae-plp-empty">
        <Styles />
        <p>{text(messages, 'listing.empty')}</p>
        <p>{text(messages, 'listing.emptyHint')}</p>
      </div>
    );
  }

  return (
    <>
      <Styles />
      {/* `layout` is the shop's own density setting, recorded rather than branched on: this
          vertical has one card, the trade card, because its showcase IS the trade counter.
          Keeping the value in the DOM means a future showcase variant is a stylesheet
          change rather than a second component. */}
      <ul className="ae ae-plp-grid" data-ae-layout={layout}>
        {items.map((item) => (
          <SolarListingCardView
            key={item.id}
            item={item}
            locale={locale}
            messages={messages}
            hrefFor={hrefFor}
            vat={vat}
          />
        ))}
      </ul>
    </>
  );
}

function Styles() {
  return (
    <>
      <TradeStyles id="base" css={BASE_CSS} />
      <TradeStyles id="listing-grid" css={LISTING_GRID_CSS} />
    </>
  );
}

interface CardProps {
  item: SolarListingCard;
  locale: string;
  messages: Record<string, string>;
  hrefFor: (publicPath: string) => string;
  vat: VatDisplayState;
}

function SolarListingCardView({ item, locale, messages, hrefFor, vat }: CardProps) {
  const t = solarText(language(locale));
  const total = totalOf([{ price: item.price as LiveMoney | undefined, quantity: 1 }]);
  const listTotal = totalOf([{ price: item.listPrice as LiveMoney | undefined, quantity: 1 }]);
  const specs = specRows(item.attributes);
  const wattage = wattageOf(item.attributes);
  const datasheet = datasheetOf(item.documents);
  const href = hrefFor(item.publicPath);

  return (
    <li className="ae-plp-card">
      <div className="ae-plp-card__media">
        {item.heroImage?.url ? (
          // eslint-disable-next-line @next/next/no-img-element -- the asset host is
          // per-tenant and not known at build time, so next/image's remote allowlist
          // cannot be configured for it.
          <img
            src={item.heroImage.url}
            {...(item.heroImage.srcSet
              ? {
                  srcSet: item.heroImage.srcSet,
                  sizes: '(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw',
                }
              : {})}
            alt={item.heroImage.alt || item.title}
            loading="lazy"
            decoding="async"
          />
        ) : (
          // No invented drawing of a product nobody has photographed: the category glyphs
          // the home rails use say "panel" whatever the part is, and on a listing that mixes
          // clamps, cable and panels that is a picture of the wrong thing.
          <span className="ae-plp-card__media--empty">{text(messages, 'product.photoComingSoon')}</span>
        )}
      </div>

      <div className="ae-plp-card__body">
        {item.brandName ? <p className="ae-plp-card__brand">{item.brandName}</p> : null}

        {/* The link wraps the TITLE, never the card: a block-level link swallows the photo's
            alt text and the price into one accessible name, and this card also holds a
            quantity box and a button, neither of which may sit inside a link at all. */}
        <h3 className="ae-plp-card__title">
          <PortalLink href={href}>{item.title}</PortalLink>
        </h3>

        {/* The part number the buyer arrived knowing. The solar shop shows it and the kitchen
            shop does not (owner, 2026-10-01); this component is the solar shop's. */}
        {item.mpn ? (
          <p className="ae-plp-card__mpn">
            <abbr title={t('plp.mpnTitle')}>{t('plp.mpn')}</abbr>
            <span className="ae-plp-card__mpn-value">{item.mpn}</span>
          </p>
        ) : null}

        {specs.length ? (
          <dl className="ae-plp-card__specs">
            {specs.map((spec) => (
              <div className="ae-plp-card__srow" key={`${spec.code}-${spec.label}`}>
                <dt>{spec.label}</dt>
                <dd>{spec.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        <div className="ae-plp-card__price">
          {total ? (
            <CardPrice
              total={total}
              listTotal={listTotal}
              wattage={wattage}
              basis={vat.basis}
              locale={locale}
              messages={messages}
              t={t}
            />
          ) : (
            <p className="ae-plp-card__poa">{text(messages, 'price.onRequest')}</p>
          )}
        </div>

        <p className="ae-plp-card__stock" data-availability={item.availability ?? 'UNKNOWN'}>
          {t(availabilityLabel(item.availability))}
        </p>

        {/* Always rendered, so a card with no document keeps the height of one that has one.
            One link, not the mockup's two: this catalogue has no SPEC_SHEET document type and
            a second link would point at the same PDF under a name nobody can check. */}
        <p className="ae-plp-card__docs">
          {datasheet ? <PortalLink href={datasheet.url}>{t('plp.datasheet')}</PortalLink> : null}
        </p>
      </div>

      <BuyFromCard item={item} messages={messages} hrefFor={hrefFor} t={t} />
    </li>
  );
}

/**
 * The price on the basis the shop is reading, the other basis beneath it, and the pence per
 * watt each of them works out at.
 *
 * <p><strong>The headline follows the header's inc/ex VAT switch.</strong> That switch is the
 * platform's, and every other price in the shop obeys it — the storefront's own card does so
 * through `<Price>`, which subscribes to the store directly. This card cannot subscribe: it
 * is in another package, and it derives a second figure from the price, which must divide the
 * very figure printed beside it. So the host reads the switch and hands the answer in as
 * `vat`, and the headline is whichever of the backend's two figures that names. A replacement
 * grid that ignored it would leave the switch moving the header and nothing else.
 *
 * <p><strong>The other basis still sits beneath it</strong>, as this theme's `LivePrice` does
 * on the home page. A trade catalogue is read by two people — a consumer, for whom the Price
 * Marking Order requires the inc VAT figure, and an installer, who will be invoiced and will
 * reclaim the ex VAT one — and the second line costs no extra request, because `alternate`
 * already travels on every unit price. Flipping the switch swaps which of the two is the
 * headline, so the card visibly follows it either way.
 *
 * <p><strong>Nothing here does tax arithmetic</strong>, and the pence per watt is computed per
 * basis rather than once: the figure beside the inc VAT price divides the inc VAT price, the
 * figure beside the ex VAT price divides the ex VAT one. A single per-watt number next to two
 * prices would be right about one of them and wrong about the other.
 *
 * <p>The per-watt line appears only where the product's own attributes state a wattage. It is
 * a display calculation over data already on the card — no backend sends it, and no field in
 * commerce is named for it.
 */
function CardPrice({
  total,
  listTotal,
  wattage,
  basis,
  locale,
  messages,
  t,
}: {
  total: LineTotal;
  listTotal: LineTotal | null;
  wattage: Wattage | null;
  basis: VatBasis;
  locale: string;
  messages: Record<string, string>;
  t: (key: string, values?: Record<string, string | number>) => string;
}) {
  const label = (inclusive: boolean) =>
    text(messages, inclusive ? 'price.taxIncluded' : 'price.taxExcluded');
  const shown = totalOnBasis(total, basis);
  // An absent list price means "no discount", which is NOT the same as one equal to the
  // price: nothing is struck through unless the backend sent a genuinely different figure,
  // on the same basis as the price beside it — which moves with the switch too, so a was
  // price is never a figure from the other basis struck through next to this one.
  const shownList = listTotal ? totalOnBasis(listTotal, basis) : null;
  const was =
    shownList &&
    shownList.currency === shown.currency &&
    shownList.shownInclusive === shown.shownInclusive &&
    shownList.shown !== shown.shown
      ? formatMinor(shownList.shown, shown.currency, locale)
      : null;

  const perWatt = wattage ? formatPerWatt(shown.shown, wattage.watts, shown.currency, locale) : null;
  const perWattAlternate =
    wattage && shown.other !== undefined
      ? formatPerWatt(shown.other, wattage.watts, shown.currency, locale)
      : null;

  return (
    <>
      <p className="ae-plp-card__price-line">
        <span className="ae-plp-card__now">{formatMinor(shown.shown, shown.currency, locale)}</span>
        {was ? (
          <span className="ae-plp-card__was">
            <span className="ae-plp-sr">{text(messages, 'price.was')} </span>
            {was}
          </span>
        ) : null}
        <span className="ae-plp-card__basis">{label(shown.shownInclusive)}</span>
      </p>
      {shown.other !== undefined ? (
        <p className="ae-plp-card__alt">
          {formatMinor(shown.other, shown.currency, locale)} {label(shown.otherInclusive!)}
        </p>
      ) : null}
      {/* The mockup discloses the calculation in a page footnote (`#fn-ppw`). A grid cannot
          add a section to a page it does not own, so the disclosure travels on the figure
          itself — the one place it is certain to be read next to the number it is about. */}
      {perWatt && wattage ? (
        // `data-ppw-from` records WHICH attribute the division used — the field id and its
        // label — so "why does this card say 2.3p a watt" is answerable from the page itself
        // rather than by rerunning the sum by hand against a catalogue export.
        <p
          className="ae-plp-card__ppw"
          title={t('plp.perWattNote')}
          data-ppw-from={wattage.code}
          data-ppw-label={wattage.label}
        >
          <span className="ae-plp-card__ppw-now">{perWatt}</span> {t('plp.perWatt')}{' '}
          {label(shown.shownInclusive)}
          {perWattAlternate ? (
            <span className="ae-plp-card__ppw-alt">
              {perWattAlternate} {label(shown.otherInclusive!)}
            </span>
          ) : null}
        </p>
      ) : null}
    </>
  );
}

/**
 * The quantity stepper and the Add, over the shared basket.
 *
 * <p><strong>The basket control stays local; the list picker is shared.</strong> Commerce's
 * `BasketStepper` is private to the storefront app, and the mockup's control is a different
 * one anyway: a ± stepper with a 40px box between two 44px
 * buttons, beside the one-line "Add" with the basket glyph the owner chose on 2026-10-01
 * (variant C). What is shared with it is the behaviour, deliberately matched: the quantity is
 * what the shopper is ABOUT to add and resets to one after a successful add, so a second click
 * adds one more rather than silently repeating a bulk quantity already committed, and what is
 * already in the basket is read from the cart and shown separately.
 *
 * <p>Without a cart it can still render for an account with saved-list capability; otherwise
 * it renders nothing, as a shop that sells by enquiry has no basket button to offer.
 */
function BuyFromCard({
  item,
  messages,
  hrefFor,
  t,
}: {
  item: SolarListingCard;
  messages: Record<string, string>;
  hrefFor: (publicPath: string) => string;
  t: (key: string, values?: Record<string, string | number>) => string;
}) {
  const cart = useCart();
  const commerce = useCommerceSource();
  const candidate = commerce && typeof commerce === 'object' && 'buyingLists' in commerce
    ? commerce.buyingLists : undefined;
  const lists = candidate && typeof candidate === 'object'
    && 'list' in candidate && typeof candidate.list === 'function'
    && 'detail' in candidate && typeof candidate.detail === 'function'
    && 'create' in candidate && typeof candidate.create === 'function'
    && 'addItems' in candidate && typeof candidate.addItems === 'function'
    && 'getScopeKey' in candidate && typeof candidate.getScopeKey === 'function'
    ? candidate as SavedListPickerSource : undefined;
  const scopeKey = lists?.getScopeKey?.();
  const [quantity, setQuantity] = useState('1');
  const [state, setState] = useState<'idle' | 'busy' | 'added' | 'failed'>('idle');

  if ((!cart || !cart.available) && !scopeKey) return null;

  const outOfStock = item.availability === 'OUT_OF_STOCK';
  // An empty or junk box means one, not zero and not NaN: the button says "add", so the
  // least surprising reading of a blank field is the smallest thing it could add.
  const parsed = Math.max(1, Math.floor(Number(quantity) || 1));
  const inBasket =
    cart?.cart?.lines
      .filter((line) => line.productId === item.id && line.parentLineId == null)
      .reduce((count, line) => count + (Number(line.quantity) || 0), 0) ?? 0;

  const step = (by: number) => setQuantity(String(Math.max(1, parsed + by)));

  async function add() {
    if (!cart?.available || state === 'busy') return;
    setState('busy');
    try {
      const added = await cart.add({ productId: item.id, quantity: String(parsed) });
      setState(added ? 'added' : 'failed');
      if (added) setQuantity('1');
    } catch {
      setState('failed');
    }
  }

  return (
    <>
      {cart?.available ? <div className="ae-plp-card__actions">
        <span className="ae-plp-card__qty">
          <button type="button" aria-label={t('plp.decrease')} disabled={parsed <= 1} onClick={() => step(-1)}>
            −
          </button>
          <input
            type="text"
            inputMode="numeric"
            value={quantity}
            aria-label={text(messages, 'basket.quantity')}
            onChange={(event) => setQuantity(event.target.value)}
          />
          <button type="button" aria-label={t('plp.increase')} onClick={() => step(1)}>
            +
          </button>
        </span>
        <button
          type="button"
          className="ae-plp-card__add"
          disabled={outOfStock || state === 'busy'}
          onClick={add}
        >
          <BasketGlyph />
          {outOfStock
            ? text(messages, 'basket.outOfStock')
            : state === 'busy'
              ? t('home.adding')
              : state === 'added'
                ? t('home.addedShort')
                : t('home.add')}
        </button>
      </div> : scopeKey ? <div className="ae-plp-card__actions">
        <span className="ae-plp-card__qty">
          <button type="button" aria-label={t('plp.decrease')} disabled={parsed <= 1} onClick={() => step(-1)}>
            −
          </button>
          <input
            type="text"
            inputMode="numeric"
            value={quantity}
            aria-label={text(messages, 'basket.quantity')}
            onChange={(event) => setQuantity(event.target.value)}
          />
          <button type="button" aria-label={t('plp.increase')} onClick={() => step(1)}>
            +
          </button>
        </span>
      </div> : null}
      {scopeKey && lists ? <SavedListPicker
        key={`${scopeKey}:${item.id}`}
        source={lists}
        scopeKey={scopeKey}
        product={{ id: String(item.id), title: item.title }}
        initialQuantity={String(parsed)}
      /> : null}
      <p className="ae-plp-card__status" role="status" aria-live="polite" data-state={state}>
        {state === 'failed' ? (
          text(messages, 'basket.error')
        ) : inBasket > 0 ? (
          <PortalLink href={hrefFor('/cart')}>
            {text(messages, 'basket.inBasket')} {inBasket}
          </PortalLink>
        ) : null}
      </p>
    </>
  );
}

function BasketGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 9h16" />
      <path d="M6 9l1.4 10.2a1 1 0 0 0 1 .8h7.2a1 1 0 0 0 1-.8L18 9" />
      <path d="M9 9l3-6 3 6" />
    </svg>
  );
}

/** One of the HOST's strings. Missing reads as the key, so a gap is visible rather than blank
 *  — the same contract `message()` keeps in the storefront, which this theme cannot import. */
function text(messages: Record<string, string>, key: string): string {
  return messages[key] ?? key;
}

/**
 * The language this theme's own catalogue is read in.
 *
 * <p>Taken from the ROUTE's locale prop rather than from `useLocale()`: the two listing routes
 * resolve the active locale themselves and hand it down, and a context default would quietly
 * disagree with the prices and the host strings on the same card.
 */
function language(locale: string): string {
  return (locale || 'en').split('-')[0]!.toLowerCase();
}
